import { AutomationEventTypes } from "../AutomationModuleTypes";
import _ from "lodash";

const vt = {
    STRING: "string",
    NUMBER: "number", 
    BOOLEAN: "boolean",
    DATE: "date",
    DATETIME: "datetime",
    TIME: "time",
    ENUM: "enum",
    ARRAY: "array",
    ARRAY_NUMBER: "array_number",
    ARRAY_STRING: "array_string",
    ARRAY_DATE: "array_date",
    ARRAY_OBJECT: "array_objectId",
    ARRAY_BOOLEAN: "array_boolean",
    OBJECT: "object",
    MIXED: "mixed",
};

const contexts = {
    CONTACT: "contact",
    CONVERSATION: "conversation",
    BLOCK: "block",
    EVENT: "event",
    WEBHOOK: "webhook",
    SYSTEM: "system",
    CUSTOM: "custom",
};

const defaultOperators = {
    string: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
    ],
    array: ["equal", "not_equal", "contains", "not_contains", "empty", "not_empty"],
    number: ["equal", "not_equal", "greater_than", "less_than", "greater_than_or_equal", "less_than_or_equal"],
    boolean: ["equal", "not_equal", "empty", "not_empty"],
    date: [
        "equal",
        "not_equal",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
        "empty",
        "not_empty",
    ],
    ObjectId: ["equal", "not_equal", "empty", "not_empty"],
    object: ["equal", "not_equal", "empty", "not_empty"],
    mixed: [
        "equal",
        "not_equal",
        "contains",
        "not_contains",
        "empty",
        "not_empty",
        "starts_with",
        "ends_with",
        "shorter_than",
        "longer_than",
        "greater_than",
        "less_than",
        "greater_than_or_equal",
        "less_than_or_equal",
    ],
};

const ga = (
    context,
    value,
    label,
    valueType = "string",
    controlType = "input",
    subAttributes = [],
    defaultFilterValue = undefined,
    defaultOperator = undefined,
    availableOperators = undefined,
    options = undefined
) => {
    return {
        context,
        name,
        value,
        label,
        defaultOperator: defaultOperator || "equal",
        defaultFilterValue,
        availableOperators: availableOperators || defaultOperators[valueType] || ["equal", "not_equal"],
        controlType,
        options,
        subAttributes,
        valueType,
    };
};

class TaskModuleAttributeFactory {
    /**
     * @param parent {AutomationAttributesGenerator}
     */
    constructor(parent) {
        this.parent = parent;
    }

    generate() {
        return _.concat(
            this.taskCreated(),
            this.taskDeleted(),
            this.taskArchived(),
            this.taskCompleted(),
            this.taskAssigneeChanged(),
            this.taskPriorityChanged(),
            this.taskStageChanged(),
            this.taskChecklistCompleted(),
            this.taskDueDateChanged(),
            this.taskTagAdded(),
            this.taskTagRemoved()
        );
    }

    taskCreated() {
        const e = AutomationEventTypes.TASK.CREATED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Created", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING)
            ])
        ];
    }

    taskDeleted() {
        const e = AutomationEventTypes.TASK.DELETED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Deleted", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING)
            ])
        ];
    }

    taskArchived() {
        const e = AutomationEventTypes.TASK.ARCHIVED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Archived", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING)
            ])
        ];
    }

    taskCompleted() {
        const e = AutomationEventTypes.TASK.COMPLETED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Completed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING)
            ])
        ];
    }

    taskAssigneeChanged() {
        const e = AutomationEventTypes.TASK.ASSIGNEE_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Assignee Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.oldAssignees`, "Old Assignees", vt.ARRAY_STRING),
                ga(context, `${prefix}.newAssignees`, "New Assignees", vt.ARRAY_STRING)
            ])
        ];
    }

    taskPriorityChanged() {
        const e = AutomationEventTypes.TASK.PRIORITY_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Priority Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.oldPriority`, "Old Priority", vt.STRING),
                ga(context, `${prefix}.newPriority`, "New Priority", vt.STRING)
            ])
        ];
    }

    taskStageChanged() {
        const e = AutomationEventTypes.TASK.STAGE_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Stage Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.oldStage`, "Old Stage", vt.STRING),
                ga(context, `${prefix}.newStage`, "New Stage", vt.STRING)
            ])
        ];
    }

    taskChecklistCompleted() {
        const e = AutomationEventTypes.TASK.CHECKLIST_COMPLETED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Checklist Completed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING)
            ])
        ];
    }

    taskDueDateChanged() {
        const e = AutomationEventTypes.TASK.DUE_DATE_CHANGED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Due Date Changed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.oldDueDate`, "Old Due Date", vt.DATETIME),
                ga(context, `${prefix}.newDueDate`, "New Due Date", vt.DATETIME)
            ])
        ];
    }

    taskTagAdded() {
        const e = AutomationEventTypes.TASK.TAG_ADDED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Tag Added", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.newTags`, "Added Tags", vt.ARRAY_STRING)
            ])
        ];
    }

    taskTagRemoved() {
        const e = AutomationEventTypes.TASK.TAG_REMOVED;
        const context = contexts.EVENT;
        const prefix = `event.${e}`;

        return [
            ga(context, prefix, "Task Tag Removed", vt.OBJECT, undefined, [
                ga(context, `${prefix}.$..*`, "*", vt.OBJECT),
                ga(context, `${prefix}.taskId`, "Task ID", vt.STRING),
                ga(context, `${prefix}.siteId`, "Site ID", vt.STRING),
                ga(context, `${prefix}.removedTags`, "Removed Tags", vt.ARRAY_STRING)
            ])
        ];
    }
}

export default TaskModuleAttributeFactory; 